import { useState, useRef, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { FormSelect } from '@podus/ui';
import ChartCardHeader from './ChartCardHeader';

export interface DonutDataItem {
    name: string;
    value: number;
    color: string;
}

export interface DonutChartCardProps {
    title: string;
    subtitle: string;
    data: DonutDataItem[];
    header?: React.ReactNode;
    innerRadius?: number;
    outerRadius?: number;
    height?: number;
    year?: number;
    onYearChange?: (year: number) => void;
    yearsBack?: number;
}

const currentYear = new Date().getFullYear();

export default function DonutChartCard({
    title,
    subtitle,
    data,
    header,
    innerRadius = 60,
    outerRadius = 100,
    height = 220,
    year,
    onYearChange,
    yearsBack = 5,
}: DonutChartCardProps) {
    const years = Array.from({ length: yearsBack + 1 }, (_, i) => currentYear - i);
    const total = data.reduce((acc, d) => acc + d.value, 0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setContainerWidth(entry.contentRect.width);
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    const scale = containerWidth > 0 ? Math.min(1, containerWidth / (outerRadius * 2.5)) : 1;
    const scaledInner = Math.round(innerRadius * scale);
    const scaledOuter = Math.round(outerRadius * scale);
    const scaledHeight = Math.round(height * scale);

    return (
        <div className="rounded-xl border border-[#eee] p-5">
            {header ?? <ChartCardHeader />}
            <h3 className="mt-1 text-center sm:text-left font-bold text-secondary text-2xl">{title}</h3>

            {onYearChange && (
                <div className="mt-3 flex justify-end">
                    <div className="w-36">
                        <FormSelect
                            title="Year"
                            value={String(year ?? currentYear)}
                            onChange={(v) => onYearChange(Number(v))}
                            options={years.map((y) => ({ label: String(y), value: String(y) }))}
                        />
                    </div>
                </div>
            )}

            <div className="mt-4 flex flex-col items-start gap-4 sm:flex-row">
                {/* Legend */}
                <div className="sm:w-[40%]">
                    <p className="text-[14px] leading-snug text-[#888]">{subtitle}</p>

                    <div className="mt-5 space-y-3">
                        {data.map((d) => (
                            <div key={d.name} className="flex items-center gap-2 text-[14px]">
                                <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: d.color }} />
                                <span className="text-secondary">
                                    {d.name} ({Math.round((d.value / total) * 100)}%)
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Donut */}
                <div ref={containerRef} className="w-full sm:w-[60%]">
                    <ResponsiveContainer width="100%" height={scaledHeight}>
                        <PieChart>
                            <Pie
                                data={data}
                                cx="50%"
                                cy="50%"
                                innerRadius={scaledInner}
                                outerRadius={scaledOuter}
                                paddingAngle={2}
                                dataKey="value"
                                stroke="none"
                            >
                                {data.map((d) => (
                                    <Cell key={d.name} fill={d.color} />
                                ))}
                            </Pie>
                            <Tooltip
                                contentStyle={{ borderRadius: 8, border: '1px solid #eee', fontSize: 13 }}
                                formatter={(value, name) => [`${Math.round((Number(value) / total) * 100)}%`, String(name)]}
                            />
                        </PieChart>
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
}
